import {AlignmentAnalysis} from 'analyses/Analyses';
import {assert} from 'Asserts';
import {DNAAlignmentAnalysisJSONAttributes} from 'constants/AlignmentConstants';
import {SequenceServerResponse} from 'constants/SequenceConstants';
import FileStates from 'FileStates';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {isServerRequestError} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface SequenceAlignmentServerResponse {
  sequence: SequenceServerResponse;
  analysis: DNAAlignmentAnalysisJSONAttributes;
}

export function maybeShowBadAlignmentError(
  parsedAnalysis: DNAAlignmentAnalysisJSONAttributes | null,
  tab: SequenceAlignmentTabState
): void {
  if (parsedAnalysis != null && parsedAnalysis.alignments == null) {
    notifications.error(
      "We're sorry, it looks like this alignment got into a bad state. Please try creating it again, or contact Benchling Support for more assistance."
    );
    tab.close();
  }
}

async function open({sequenceId, alignmentId}: {sequenceId: string; alignmentId: string}): Promise<void> {
  let tab;
  try {
    const fileState = await FileStates.get().openFolderItem(sequenceId, {open: true});
    assert(fileState != null, 'Expected non-null fileState');
    tab = fileState.findTab(
      SequenceAlignmentTabState,
      (t: SequenceAlignmentTabState) => t.get('alignmentId') === alignmentId
    );
    if (tab) {
      tab.set('active', true);
    } else {
      LoadingScreen.showFileLoading();

      const sequence = fileState.get('sequence');
      const analysis = sequence
        .get('analyses')
        .find((a: AlignmentAnalysis) => a.get('apiId') === alignmentId);
      if (!analysis) {
        return;
      }

      tab = new SequenceAlignmentTabState({sequence, alignmentId});
      fileState.buffers().addTab(tab, {side: 'left'});
      fileState.setIsWorkspaceSplit(false);
      const parsedAlignment = await analysis.fetch({parse: true});
      maybeShowBadAlignmentError(parsedAlignment, tab);
      if (parsedAlignment?.alignments == null) {
        LoadingScreen.hideFileLoading();
        return;
      }
      tab.loadAnalysis(analysis);
    }
  } catch (e) {
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
      if (tab) {
        tab.close();
      }
      return;
    }
    throw e;
  } finally {
    LoadingScreen.hideFileLoading();
  }
}

export async function exportSourceFiles(alignmentIds: Array<string>): Promise<void> {
  const TOAST_ID = 'alignment-source-export';
  notifications.info('Preparing export...', {id: TOAST_ID, duration: 0});
  try {
    const response = await TaskRunner.postJSONPromise({
      url: '/1/api/sequence/analyses:get-attached-files',
      data: {analysisIds: alignmentIds},
    });
    window.location = response.url;
    notifications.success('Export complete. Downloading...', {id: TOAST_ID});
  } catch (e) {
    notifications.close(TOAST_ID);
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
      return;
    }
    throw e;
  }
}

export {open};
